import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { t } from "i18next";

import { Button } from "primereact/button";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";

import { isValidHexColor } from "../../../../utils/functions/normalizeHex";
import { sharedDialog, useSharedDialogState } from "../../../components/Shared/Components/Dialogs/SharedDialog";
import { translationKeys } from "../../../models/app-translation-keys";
import { useStore } from "../../../stores/StoreContext";
import { IBackgroundDialogState } from "./imageBackgroundDialogTypes";

interface IImageBackgroundDialogFooterProps {
  dialogId: string | undefined;
  backgroundColor: string | undefined;
  backgroundColorEnabled: boolean;
  setBackgroundColor: Dispatch<SetStateAction<string | undefined>>;
  setBackgroundColorEnabled: Dispatch<SetStateAction<boolean>>;
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const backgroundControlTranslationKeys = featureTranslationKeys.backgroundControl;

export const ImageBackgroundDialogFooter: FunctionComponent<IImageBackgroundDialogFooterProps> = ({
  dialogId,
  backgroundColor,
  backgroundColorEnabled,
  setBackgroundColor,
  setBackgroundColorEnabled,
}: IImageBackgroundDialogFooterProps) => {
  const { globalErrorsStore } = useStore();
  const { sharedDialogState, setSharedDialogState } = useSharedDialogState<IBackgroundDialogState>();

  const applyEmptyValues = () => {
    setSharedDialogState(previousState => ({ ...previousState, draftColor: "", isDraftColorInvalid: false }));
    globalErrorsStore.removeError(featureTranslationKeys.errors.invalidColor);
  };

  const handleCheckboxToggle = (event: CheckboxChangeEvent) => {
    setSharedDialogState(previousState => ({
      ...previousState,
      draftEnabled: event.target.checked ?? false,
    }));

    if (!event.target.checked) {
      applyEmptyValues();
    }
  };

  const handleFooterCancel = () => {
    if (!isValidHexColor(sharedDialogState.draftColor)) {
      applyEmptyValues();
    }

    if (dialogId) {
      sharedDialog.closeSharedDialog(dialogId);
    }
  };

  const handleFooterSelect = () => {
    setBackgroundColorEnabled(sharedDialogState.draftEnabled);

    if (!sharedDialogState.draftColor || !sharedDialogState.draftEnabled) {
      setBackgroundColor(undefined);
    } else if (isValidHexColor(sharedDialogState.draftColor)) {
      setBackgroundColor(sharedDialogState.draftColor);
    } else {
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidColor);
      console.error("[ImageBackgroundControl] Invalid image background color");
    }

    if (dialogId) {
      sharedDialog.closeSharedDialog(dialogId);
    }
  };

  return (
    <div className="dialog-footer">
      <div className="dialog-footer-left">
        <Checkbox
          inputId="enable-background-checkbox"
          className="enable-background"
          data-testid="image-background-enable-checkbox"
          checked={sharedDialogState.draftEnabled}
          onChange={handleCheckboxToggle}
        />

        <label htmlFor="enable-background-checkbox" className="dialog-footer-label">
          {t(backgroundControlTranslationKeys.enableTooltip)}
        </label>
      </div>

      <div className="dialog-footer-buttons">
        <Button
          label={t(translationKeys.actions.cancel)}
          data-testid="image-background-cancel-button"
          onClick={handleFooterCancel}
          text
        />

        <Button
          label={t(translationKeys.actions.apply)}
          data-testid="image-background-apply-button"
          severity="success"
          disabled={
            sharedDialogState.isDraftColorInvalid ||
            (sharedDialogState.draftColor === backgroundColor &&
              sharedDialogState.draftEnabled === backgroundColorEnabled)
          }
          onClick={handleFooterSelect}
        />
      </div>
    </div>
  );
};
